import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc } from 'firebase/firestore';
import { auth, functions, db } from '../firebase';
import { NEW_ACCOUNT_PREFS } from '../hooks/useUserPrefs';
import { initialAuthMode } from '../utils/authMode';
import Logo from '../components/Logo';
import AuthArtPanel from '../components/AuthArtPanel';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MIN_PASSWORD = 8; // NIST-style: length over forced complexity.

// Allowlist of relative paths that we'll honor in the ?next= param.
// Defensive: ?next= could be set by anything that links to /login,
// and we don't want to bounce users to arbitrary external URLs.
function safeNextPath(raw) {
  if (!raw) return null;
  if (!/^\/[a-zA-Z]/.test(raw)) return null;
  return raw;
}

// ── Claim deeplink context ───────────────────────────────────
// When a customer scans the QR on their unit, PrivateRoute bounces
// them here as /login?next=/claim?d=WT-0042&t=XXXX. Parse that so we
// can pin the device they're setting up above the auth form — the
// auth step then reads as part of claiming, not a wall. Mirrors the
// validation in ClaimDevice.js. NOTE: this is a format check only; the
// claim code is verified server-side (claimDevice) AFTER auth, so we
// don't claim it's "verified" here.
const DEVICE_ID_RE   = /^WT-\d{4,5}$/;
const CLAIM_TOKEN_RE = /^[A-Z0-9]{11}$/;
function normalizeToken(t) { return String(t || '').replace(/[-\s]/g, '').toUpperCase(); }
function formatToken(t) {
  const n = normalizeToken(t);
  return n.length === 11 ? `${n.slice(0, 4)}-${n.slice(4, 8)}-${n.slice(8)}` : n;
}
function parseClaimContext(nextPath) {
  if (!nextPath || !nextPath.startsWith('/claim')) return null;
  const q = nextPath.indexOf('?');
  if (q < 0) return null;
  const p = new URLSearchParams(nextPath.slice(q + 1));
  const deviceId = (p.get('d') || '').trim().toUpperCase();
  const claimToken = normalizeToken(p.get('t'));
  if (!deviceId || !claimToken) return null;
  return { deviceId, claimToken, wellFormed: DEVICE_ID_RE.test(deviceId) && CLAIM_TOKEN_RE.test(claimToken) };
}

// Physical tank-monitor unit icon (signal + enclosure).
function IconMonitor() {
  return (
    <svg viewBox="0 0 24 24" fill="none"><rect x="4" y="9" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" /><circle cx="12" cy="14.5" r="1.6" fill="currentColor" /><path d="M8.5 5.5a5 5 0 0 1 7 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M10.6 7.7a2.2 2.2 0 0 1 2.8 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get('next'));
  const claim = parseClaimContext(nextPath);
  // Set only by the share-invite email, and only when shareDevice already
  // confirmed server-side that this recipient has no account yet.
  const invited = searchParams.get('new') === '1';

  // 'create' — make a new account (email + password + confirm).
  // 'signin' — existing account. The default for every arrival that isn't
  //            demonstrably new; see utils/authMode.js for why the URL is
  //            enough to tell them apart.
  // 'reset'  — send a password reset link.
  const [mode, setMode] = useState(() => initialAuthMode({ nextPath, newHint: invited }));
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [message, setMessage]   = useState('');
  const [loading, setLoading]   = useState(false);

  function clearMessages() { setError(''); setMessage(''); }

  /** Switch form without losing the email they already typed or the ?next=. */
  function switchMode(next) {
    setMode(next);
    setPassword('');
    setConfirm('');
    clearMessages();
  }

  /**
   * Seed the preferences a brand-new account starts with. Best-effort: a
   * failure here must never block sign-up, since the hook falls back to a
   * working default either way.
   */
  async function seedNewAccountPrefs(uid) {
    try {
      await setDoc(doc(db, 'users', uid), { ...NEW_ACCOUNT_PREFS }, { merge: true });
    } catch (err) {
      console.error('Could not seed new-account prefs:', err);
    }
  }

  // ── Create account ───────────────────────────────────────────
  //
  // Explicit, rather than the previous create-or-sign-in-in-one-go. That trick
  // was enumeration-safe but made the form say "enter or create a password",
  // which reads as a riddle to someone holding a new device.
  //
  // Enumeration safety is preserved differently: Firebase's email-enumeration
  // protection returns a GENERIC error for an address that already exists, so
  // we never learn (or state) that it does. We offer the sign-in switch as a
  // suggestion, which is equally useful whether or not the account is real.
  async function handleCreate(e) {
    e.preventDefault();
    clearMessages();
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (cred?.user?.uid) await seedNewAccountPrefs(cred.user.uid);
      navigate(nextPath || '/dashboard');
      return;
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/weak-password') {
        setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again in a few minutes.');
      } else {
        // Covers email-already-in-use AND the generic enumeration-protected
        // error, which are indistinguishable by design. Same advice fits both.
        setError('We could not create an account with that email. If you already have one, log in instead.');
      }
      setLoading(false);
    }
  }

  // ── Sign in ──────────────────────────────────────────────────
  async function handleSignIn(e) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate(nextPath || '/dashboard');
      return;
    } catch (err) {
      if (err?.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again in a few minutes.');
      } else {
        setError("That email and password don't match. Try again, or reset your password below.");
      }
      setLoading(false);
    }
  }

  // Password reset. Sends through our own `sendPasswordReset` cloud
  // function (branded email from support@sokowatersystems.com via Resend)
  // instead of Firebase's default noreply@…firebaseapp.com sender. The
  // function swallows user-not-found and returns success, so we always
  // report the same generic message and never reveal whether the email is
  // registered.
  async function handleReset(e) {
    e.preventDefault();
    clearMessages();
    const cleanEmail = email.trim();
    if (!EMAIL_RE.test(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const sendReset = httpsCallable(functions, 'sendPasswordReset');
      await sendReset({ email: cleanEmail });
    } catch (err) {
      // invalid-argument means the email failed server-side validation;
      // everything else is a real send/network failure worth a retry.
      // Neither path reveals account existence.
      if (err?.code === 'functions/invalid-argument') {
        setError('Please enter a valid email address.');
      } else {
        console.error('sendPasswordReset failed:', err);
        setError('Something went wrong sending the reset link. Please try again in a moment.');
      }
      setLoading(false);
      return;
    }
    setMessage('If an account exists for that email, a password reset link is on its way. Check your inbox.');
    setLoading(false);
  }

  const onSubmit = mode === 'create' ? handleCreate
    : mode === 'signin' ? handleSignIn
    : handleReset;

  return (
    <div className="login-shell">

      {/* Left — brand / value panel (desktop only) */}
      <AuthArtPanel />

      {/* Right — auth form */}
      <div className="login-form-wrap">
        <div className="login-form-inner">
          <div className="login-form-brand">
            <Logo variant="primary" width={170} />
          </div>

          {claim && mode !== 'reset' && (
            <div className="login-claim">
              <div className="login-claim__ico"><IconMonitor /></div>
              <div>
                <div className="login-claim__eyebrow">You're setting up</div>
                <div className="login-claim__name">Tank monitor</div>
                <div className="login-claim__ids">
                  <div className="login-claim__id"><span>Device ID</span><b>{claim.deviceId}</b></div>
                  <div className="login-claim__id"><span>Claim code</span><b>{formatToken(claim.claimToken)}</b></div>
                </div>
              </div>
            </div>
          )}

          <h1>
            {mode === 'create' && 'Create your account'}
            {mode === 'signin' && 'Log in'}
            {mode === 'reset'  && 'Reset password'}
          </h1>
          <p className="login-lead">
            {mode === 'create' && (claim
              ? 'This tank monitor will be linked to the account you create.'
              : invited
                ? 'Use the email address your invite was sent to and the shared tank will be waiting in your dashboard.'
                : 'Pick an email and password to get started.')}
            {mode === 'signin' && (claim
              ? 'Log in and this tank monitor will be added to your account.'
              : 'Welcome back.')}
            {mode === 'reset'  && "We'll email you a link to set a new password."}
          </p>

          {error && (
            <div className="alert-banner fault" style={{ marginBottom: 14 }}>
              <div className="alert-banner-desc">{error}</div>
            </div>
          )}
          {message && (
            <div className="toast" style={{ marginBottom: 14 }}>
              <span>✓</span> {message}
            </div>
          )}

          <form onSubmit={onSubmit}>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            {mode !== 'reset' && (
              <div className="field">
                <label>Password</label>
                <input
                  type="password"
                  placeholder={mode === 'create' ? `At least ${MIN_PASSWORD} characters` : 'Your password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  /* new-password tells a password manager to OFFER one on
                     sign-up, and not to autofill a saved one into a field the
                     person is choosing. */
                  autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
                />
              </div>
            )}

            {mode === 'create' && (
              <div className="field">
                <label>Confirm password</label>
                <input
                  type="password"
                  placeholder="Type it again"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                />
                {confirm.length > 0 && password !== confirm && (
                  <div className="hint hint--warn">Passwords don&apos;t match yet.</div>
                )}
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {mode === 'create' && (loading ? 'Creating your account…' : 'Create account')}
              {mode === 'signin' && (loading ? 'Logging in…' : 'Log in')}
              {mode === 'reset'  && (loading ? 'Sending…' : 'Send reset link')}
            </button>
          </form>

          {/* Mode switch. Nothing navigates: the ?next= claim/share context
              lives in the URL, so staying on this route keeps it intact. */}
          <div className="login-switch">
            {mode === 'create' && (
              <p className="login-switch__line">
                Already have an account?{' '}
                <button type="button" className="login-switch__primary" onClick={() => switchMode('signin')}>
                  Log in here
                </button>
              </p>
            )}
            {mode === 'signin' && (
              <>
                <p className="login-switch__line">
                  New to SoKo?{' '}
                  <button type="button" className="login-switch__primary" onClick={() => switchMode('create')}>
                    Create an account
                  </button>
                </p>
                <button type="button" className="login-switch__secondary" onClick={() => switchMode('reset')}>
                  Forgot password?
                </button>
              </>
            )}
            {mode === 'reset' && (
              <button type="button" className="login-switch__primary" onClick={() => switchMode('signin')}>
                ← Back to log in
              </button>
            )}
          </div>

          <p className="login-foot-line">SoKo Water Systems · Know your water.</p>
          <p className="login-legal-line">
            <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a>
            <span aria-hidden="true"> · </span>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
